import { PAYMENT_URL } from "../constants";

export type SeatPlanKey = "growth" | "professional";
export type BillingInterval = "monthly" | "quarterly" | "yearly";

export const SEAT_LIMITS: Record<
  SeatPlanKey,
  { min: number; max: number; included: number; extraSeatMonthly: number }
> = {
  growth: { min: 1, max: 1, included: 1, extraSeatMonthly: 20 },
  professional: { min: 5, max: 999, included: 5, extraSeatMonthly: 10 },
};

const INTERVAL_DISCOUNT: Record<BillingInterval, number> = {
  monthly: 0,
  quarterly: 0.15,
  yearly: 0.25,
};

const PLAN_DISPLAY_MONTHLY: Record<SeatPlanKey, Record<BillingInterval, number>> = {
  growth: { monthly: 20, quarterly: 20, yearly: 20 },
  professional: { monthly: 50, quarterly: 50, yearly: 50 },
};

export function normalizeSeatPlanKey(planKey: string): SeatPlanKey {
  return planKey === "professional" ? "professional" : "growth";
}

export function getPlanIncludedSeats(planKey: string): number {
  return SEAT_LIMITS[normalizeSeatPlanKey(planKey)].included;
}

/** Professional can add seats above included; Solo is fixed at 1. */
export function canAdjustSeatCount(planKey: string): boolean {
  const limits = SEAT_LIMITS[normalizeSeatPlanKey(planKey)];
  return limits.max > limits.min;
}

export function resolveSeatCount(planKey: string, rawSeatCount: unknown): number {
  const key = normalizeSeatPlanKey(planKey);
  const limits = SEAT_LIMITS[key];
  const parsed = Number(rawSeatCount);
  if (!Number.isFinite(parsed) || parsed <= 0) return limits.included;
  return Math.min(Math.max(Math.floor(parsed), limits.min), limits.max);
}

export function computeTotalPlanPriceMonthly(
  planKey: string,
  interval: string,
  seatCount: number
): number | null {
  const key = normalizeSeatPlanKey(planKey);
  const billingInterval: BillingInterval =
    interval === "quarterly" || interval === "yearly" ? interval : "monthly";
  const limits = SEAT_LIMITS[key];
  const clamped = resolveSeatCount(key, seatCount);
  const baseDisplay = PLAN_DISPLAY_MONTHLY[key][billingInterval];

  if (!canAdjustSeatCount(key)) {
    return baseDisplay;
  }

  const discount = INTERVAL_DISCOUNT[billingInterval];
  const extraSeats = Math.max(0, clamped - limits.included);
  const extraDisplayMonthly = Math.floor(limits.extraSeatMonthly * (1 - discount));
  return baseDisplay + extraSeats * extraDisplayMonthly;
}

const PLAN_BILLED_BASE: Record<SeatPlanKey, Record<Exclude<BillingInterval, "monthly">, number>> = {
  growth: { quarterly: 20, yearly: 20 },
  professional: { quarterly: 50, yearly: 50 },
};

export function resolveDefaultTargetSeatCount(
  currentPlanKey: string,
  targetPlanKey: string,
  currentSeatCount: number
): number {
  const currentKey = normalizeSeatPlanKey(currentPlanKey);
  const targetKey = normalizeSeatPlanKey(targetPlanKey);
  const targetLimits = SEAT_LIMITS[targetKey];

  if (!canAdjustSeatCount(targetKey)) {
    return targetLimits.included;
  }

  const current = resolveSeatCount(currentKey, currentSeatCount);
  if (currentKey === targetKey) return current;

  return Math.min(Math.max(current, targetLimits.included), targetLimits.max);
}

export function computeBilledLine(
  planKey: string,
  interval: string,
  seatCount: number
): string {
  const key = normalizeSeatPlanKey(planKey);
  const billingInterval: BillingInterval =
    interval === "quarterly" || interval === "yearly" ? interval : "monthly";
  if (billingInterval === "monthly") return "";

  const limits = SEAT_LIMITS[key];
  const clamped = resolveSeatCount(key, seatCount);

  if (!canAdjustSeatCount(key)) {
    const totalBilled = PLAN_BILLED_BASE[key][billingInterval];
    return billingInterval === "quarterly"
      ? `Billed $${totalBilled} quarterly`
      : `Billed $${totalBilled} yearly`;
  }

  const discount = INTERVAL_DISCOUNT[billingInterval];
  const intervalMonths = billingInterval === "quarterly" ? 3 : 12;
  const extraSeats = Math.max(0, clamped - limits.included);
  const extraBilled = Math.floor(limits.extraSeatMonthly * (1 - discount) * intervalMonths);
  const baseBilled = PLAN_BILLED_BASE[key][billingInterval];
  const totalBilled = baseBilled + extraSeats * extraBilled;
  return billingInterval === "quarterly"
    ? `Billed $${totalBilled} quarterly`
    : `Billed $${totalBilled} yearly`;
}

export function formatPlanDisplayName(planKey: string): string {
  return planKey === "professional" ? "Professional" : "Solo";
}

export function resolvePlanAmountDisplay(plan: {
  planKey?: string;
  interval?: string;
  seatCount?: number | null;
  totalPlanPrice?: string | null;
  planPrice?: string | null;
}): { amount: number; suffix: string } {
  const planKey = String(plan?.planKey || "growth").toLowerCase();
  const interval = String(plan?.interval || "monthly").toLowerCase();
  const seatCount = resolveSeatCount(planKey, plan?.seatCount);
  const storedTotal = Number(plan?.totalPlanPrice);
  const computed =
    planKey === "growth" || planKey === "professional"
      ? computeTotalPlanPriceMonthly(planKey, interval, seatCount)
      : null;
  const fallbackBase = Number(plan?.planPrice);

  const amount =
    Number.isFinite(storedTotal) && storedTotal > 0
      ? storedTotal
      : computed != null
      ? computed
      : Number.isFinite(fallbackBase)
      ? fallbackBase
      : 0;

  return {
    amount,
    suffix: "/ mo",
  };
}

export async function updateSeats(params: {
  ownerId?: string;
  userId?: string;
  seatCount: number;
}) {
  const resp = await fetch(`${PAYMENT_URL}/updateSeats`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  const data = await resp.json();
  if (!resp.ok) {
    throw new Error(data?.error || "Unable to update seats");
  }
  return data as {
    ok: boolean;
    seatCount: number;
    previousSeatCount?: number;
    unchanged?: boolean;
    message?: string;
  };
}
